import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle, Clock, Search, Download, Eye, User, FileText, Loader2, X, Trash2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown,
} from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { policyService, acknowledgmentService } from "../../../api/services/complianceService";
import departmentRoleService from "../../../api/services/departmentRoleService";
import { toast } from 'react-hot-toast';
import { formatDate } from "../../../utils/stringUtils";
import { formatIndianNumber } from "../../../utils/amountFormatter";
import {
  exportToCSV,
  downloadFromBackend,
  generateExportFilename,
} from "../../../utils/exportUtils";
import ExportModal from "../../../components/common/ExportModal";
import DateRangeFilter from "../../../components/common/DateRangeFilter";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import { API_BASE_URL } from "../../../api/config";

const AcknowledgmentsNew = ({ onBack, userRole, initialMode, id, onClose, isDrawer }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isListView = !isDrawer && !initialMode;
  const initialMount = React.useRef(true);
  const isInitialFilterMount = useRef(true);
  const isInitialSortResetMount = useRef(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || "");
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || "acknowledged");
  const [filterPolicy, setFilterPolicy] = useState(searchParams.get('policy') || "all");
  const [filterDepartment, setFilterDepartment] = useState(searchParams.get('department') || "all");
  const [selectedAcknowledgment, setSelectedAcknowledgment] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRowsData, setSelectedRowsData] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(Number(searchParams.get('limit')) || 20);
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || "");
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || "");
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const selectedRows = useMemo(() => Object.keys(selectedRowsData), [selectedRowsData]);

  useEffect(() => {
    if (selectedRows.length === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.length]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Sorting state
  const [sortField, setSortField] = useState(searchParams.get('sortBy') || null);
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || "desc");


  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // React Query: Fetch policies
  const { data: policiesResponse, isLoading: policiesLoading } = useQuery({
    queryKey: ['policies'],
    queryFn: async () => {
      const response = await policyService.getPolicies();
      return response;
    },
    staleTime: 0,
  });

  // Process policies data
  const policiesData = useMemo(() => {
    if (!policiesResponse) return [];
    const payload = policiesResponse?.data || policiesResponse;
    const arr = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.policies)
          ? payload.policies
          : [];
    return arr.map((p) => ({
      id: p.id || p._id || p.policyId,
      title: p.title,
      category: p.category || "",
    }));
  }, [policiesResponse]);

  // Compute selected policy ID
  const selectedPolicyId = useMemo(() => {
    if (filterPolicy && filterPolicy !== "all") {
      return filterPolicy;
    }
    return "all";
  }, [filterPolicy]);

  // Set first policy as default on mount when no policy in URL
  useEffect(() => {
    if (initialMount.current && policiesData.length > 0) {
      const urlPolicy = searchParams.get('policy');
      if (!urlPolicy || urlPolicy === 'all') {
        setFilterPolicy(policiesData[0].id);
      } else if (!policiesData.some((p) => p.id === urlPolicy)) {
        setFilterPolicy(policiesData[0].id);
      }
      initialMount.current = false;
    }
  }, [policiesData, searchParams]);

  // React Query: Fetch departments from HR API
  const { data: departmentsResponse } = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: async () => {
      const response = await departmentRoleService.getDepartments({ isActive: 'all' });
      return response;
    },
    staleTime: 0,
  });

  // Process departments data
  const allDepartments = useMemo(() => {
    if (!departmentsResponse) return [];
    const payload = departmentsResponse?.data || departmentsResponse;
    const departments = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.departments)
          ? payload.departments
          : [];

    // Extract department names and sort
    return departments
      .map((dept) => dept.name || dept)
      .filter((name) => name && name.trim())
      .sort();
  }, [departmentsResponse]);

  // React Query: Fetch acknowledgments
  const {
    data: acknowledgmentsResponse,
    isLoading: acknowledgmentsLoading,
    isFetching: acknowledgmentsFetching
  } = useQuery({
    queryKey: ['acknowledgments', page, pageSize, filterStatus, selectedPolicyId, filterDepartment, debouncedSearchTerm, startDate, endDate],
    queryFn: async () => {
      const response = await acknowledgmentService.getAcknowledgments({
        page,
        limit: pageSize,
        status: filterStatus,
        policyId: selectedPolicyId,
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        search: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      return response;
    },
    staleTime: 0,
  });

  const baseFilters = useMemo(() => ({
    status: filterStatus !== 'all' ? filterStatus : undefined,
    policyId: selectedPolicyId !== 'all' ? selectedPolicyId : undefined,
    department: filterDepartment !== 'all' ? filterDepartment : undefined,
    search: debouncedSearchTerm || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  }), [filterStatus, selectedPolicyId, filterDepartment, debouncedSearchTerm, startDate, endDate]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'policy-acknowledgments',
            format,
            scope,
            filters: overrideFilters ?? baseFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? pageSize,
            sortBy: sortField || 'createdAt',
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `policy-acknowledgments-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseFilters, sortField, sortDirection, page, pageSize]
  );

  const handleExportClick = () => {
    if (selectedRows.length > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.length) {
        toast.error('No selected acknowledgments found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: selectedRows,
        });
        toast.success(`${selectedRows.length} acknowledgment(s) exported!`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      try {
        const options = { scope, format };
        if (scope === 'all') {
          options.overrideFilters = {};
          options.overridePage = 1;
          options.overrideLimit = 1000;
        } else if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          options.overrideFilters = {
            ...baseFilters,
            startDate: meta.startDate,
            endDate: meta.endDate,
          };
        }
        await requestBackendExport(options);
        toast.success('Export started successfully!');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport, baseFilters]
  );

  // Process acknowledgments data
  const acknowledgments = useMemo(() => {
    if (!acknowledgmentsResponse) return [];
    const payload = acknowledgmentsResponse?.data || acknowledgmentsResponse;
    const acknowledgmentsData = Array.isArray(payload?.items)
      ? payload.items
      : Array.isArray(payload)
        ? payload
        : [];

    return acknowledgmentsData.map((ack) => ({
      ...ack,
      acknowledgedDate: ack.acknowledgedDate
        ? formatDate(ack.acknowledgedDate, { includeTime: false })
        : null,
    }));
  }, [acknowledgmentsResponse]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedAcknowledgments = useMemo(() => {
    if (!sortField) return acknowledgments;

    return [...acknowledgments].sort((a, b) => {
      let aValue, bValue;

      // Map display fields to actual data fields if necessary
      switch (sortField) {
        case "employee":
          aValue = a.employeeName || "";
          bValue = b.employeeName || "";
          break;
        case "department":
          aValue = a.employeeDepartment || a.department || "";
          bValue = b.employeeDepartment || b.department || "";
          break;
        case "policy":
          aValue = a.policyTitle || "";
          bValue = b.policyTitle || "";
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        default:
          aValue = a[sortField] || "";
          bValue = b[sortField] || "";
      }

      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();

      if (sortDirection === "asc") {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  }, [acknowledgments, sortField, sortDirection]);

  useEffect(() => {
    if (id && initialMode === "view") {
      const ack = acknowledgments.find(a => (a.id || a._id) === id);
      if (ack) {
        setSelectedAcknowledgment(ack);
        setShowDetails(true);
      }
    }
  }, [id, initialMode, acknowledgments.length]);

  // Extract pagination info
  const { totalItems, totalPages } = useMemo(() => {
    if (!acknowledgmentsResponse) return { totalItems: 0, totalPages: 1 };
    const payload = acknowledgmentsResponse?.data || acknowledgmentsResponse;
    const pagination = payload?.pagination;
    if (pagination) {
      return {
        totalItems: pagination.totalItems || 0,
        totalPages: pagination.totalPages || 1,
      };
    }
    return {
      totalItems: acknowledgments.length,
      totalPages: 1,
    };
  }, [acknowledgmentsResponse, acknowledgments]);

  // React Query: Fetch stats
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['acknowledgments-stats', selectedPolicyId, filterDepartment, startDate, endDate],
    queryFn: async () => {
      if (!selectedPolicyId || selectedPolicyId === 'all') return { overall: { totalAcknowledgments: 0, acknowledged: 0, pending: 0 } };

      const params = {
        department: filterDepartment !== "all" ? filterDepartment : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        policyId: selectedPolicyId
      };

      const resp = await acknowledgmentService.getAcknowledgmentStats(params);
      return resp;
    },
    enabled: !!selectedPolicyId && selectedPolicyId !== 'all',
    staleTime: 0,
  });

  // Process stats data
  const kpiTotals = useMemo(() => {
    if (!statsResponse) return { total: 0, acknowledged: 0, pending: 0 };
    const payload = statsResponse?.data || statsResponse;
    const overall = payload?.overall || payload;
    if (overall) {
      return {
        total: overall.totalAcknowledgments ?? overall.total ?? 0,
        acknowledged: overall.acknowledged ?? 0,
        pending: overall.pending ?? 0,
      };
    }
    return { total: 0, acknowledged: 0, pending: 0 };
  }, [statsResponse]);

  const loading = policiesLoading || acknowledgmentsLoading || acknowledgmentsFetching || statsLoading;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRowsData(prev => {
        const next = { ...prev };
        sortedAcknowledgments.forEach(ack => {
          const id = ack.id || ack._id;
          next[id] = ack;
        });
        return next;
      });
    } else {
      setSelectedRowsData({});
    }
  };

  const handleSelectRow = (item) => {
    const id = item.id || item._id;
    setSelectedRowsData(prev => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = item;
      }
      return next;
    });
  };


  // Local export handlers removed in favor of unified architecture

  // Handle bulk delete
  const handleBulkDelete = async () => {
    const selectedIds = Object.keys(selectedRowsData);
    if (selectedIds.length === 0) return;

    setBulkDeleteLoading(true);
    let successCount = 0;
    let failCount = 0;

    for (const id of selectedIds) {
      try {
        await acknowledgmentService.deleteAcknowledgment(id);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }

    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRowsData({});

    if (successCount > 0) {
      toast.success(`${successCount} record(s) deleted successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      queryClient.invalidateQueries({ queryKey: ['acknowledgments'] });
      queryClient.invalidateQueries({ queryKey: ['acknowledgments-stats'] });
    } else {
      toast.error('Failed to delete selected records');
    }
  };


  // Reset page when filters change (skip initial mount so URL page is preserved)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearchTerm, filterStatus, filterPolicy, filterDepartment, startDate, endDate]);

  // Sync filter/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    if (!isListView) return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      const managedKeys = [
        'search',
        'status',
        'policy',
        'department',
        'startDate',
        'endDate',
        'page',
        'limit',
        'sortBy',
        'sortOrder',
      ];
      managedKeys.forEach((key) => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterStatus && filterStatus !== 'acknowledged') params.set('status', filterStatus);
      if (filterPolicy && filterPolicy !== 'all') params.set('policy', filterPolicy);
      if (filterDepartment && filterDepartment !== 'all') params.set('department', filterDepartment);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (page && page !== 1) params.set('page', String(page));
      if (pageSize && pageSize !== 20) params.set('limit', String(pageSize));
      if (sortField) params.set('sortBy', sortField);
      if (sortField && sortDirection && sortDirection !== 'asc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterStatus,
    filterPolicy,
    filterDepartment,
    startDate,
    endDate,
    page,
    pageSize,
    sortField,
    sortDirection,
    isListView,
    setSearchParams,
  ]);

  useEffect(() => {
    if (isInitialSortResetMount.current) {
      isInitialSortResetMount.current = false;
      return;
    }
    setSortField(null);
    setSortDirection("asc");
  }, [filterStatus, filterPolicy, filterDepartment]);

  const defaultPolicyId = useMemo(
    () => (policiesData.length > 0 ? policiesData[0].id : "all"),
    [policiesData]
  );

  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== "" ||
      filterStatus !== "acknowledged" ||
      filterPolicy !== defaultPolicyId ||
      filterDepartment !== "all" ||
      startDate !== "" ||
      endDate !== ""
    );
  }, [searchTerm, filterStatus, filterPolicy, defaultPolicyId, filterDepartment, startDate, endDate]);

  const truncate = (text, max = 32) => {
    if (!text) return "";
    return text.length > max ? text.slice(0, max - 1) + "…" : text;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Policy Acknowledgments
              </h1>
              <p className="text-gray-600">
                Track employee policy acknowledgments and compliance
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {Object.keys(selectedRowsData).length > 0 && (
                <PermissionGate permission="compliance_policy" action="delete">
                  <Button
                    variant="danger"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    loading={bulkDeleteLoading}
                  >
                    Delete Selected ({Object.keys(selectedRowsData).length})
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permission="compliance_policy" action="read">
                <Button
                  variant="outline"
                  onClick={handleExportClick}
                  className={`flex items-center space-x-2 ${selectedRows.length > 0 ? "bg-blue-50 border-blue-200 text-blue-600" : ""}`}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span>{selectedRows.length > 0 ? `Export Selected (${selectedRows.length})` : "Export"}</span>
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - DYNAMIC DATA */}
      <div className="p-6 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">
                  Total Acknowledgements
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatIndianNumber(kpiTotals.total)}
                </p>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">
                  Acknowledged
                </p>
                <p className="text-2xl font-bold text-green-900">
                  {formatIndianNumber(kpiTotals.acknowledged)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {formatIndianNumber(kpiTotals.pending)}
                </p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4 w-full">
          {/* Search Bar */}
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search employees, policies..."
              value={searchTerm}
              onChange={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => {
                // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              maxLength={100}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  // Maintain focus on input
                  const input = document.activeElement;
                  if (input && input.tagName === 'INPUT') {
                    setTimeout(() => input.focus(), 0);
                  }
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="acknowledged">Acknowledged</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Policy Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterPolicy}
              onChange={(e) => setFilterPolicy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green max-w-[250px]"
            >
              {policiesData.length > 0 && policiesData.map((policy) => (
                <option key={policy.id} value={policy.id}>
                  {policy.title}
                </option>
              ))}
            </select>
          </div>

          {/* Department Filter */}
          <div className="flex-shrink-0">
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green max-w-[200px]"
            >
              <option value="all">All Departments</option>
              {allDepartments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>

          {/* Created Date Filter */}
          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={startDate}
              endDate={endDate}
              onChange={({ startDate, endDate }) => {
                setStartDate(startDate);
                setEndDate(endDate);
              }}
              containerClassName="flex items-center space-x-2"
            />
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('acknowledged');
                  setFilterDepartment('all');
                  setStartDate('');
                  setEndDate('');
                  setFilterPolicy(defaultPolicyId);
                  setSortField(null);
                  setSortDirection("asc");
                }}
                className="flex items-center space-x-2"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Individual Acknowledgments Table */}
      <div className="bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={sortedAcknowledgments.length > 0 && sortedAcknowledgments.every(a => !!selectedRowsData[a.id || a._id])}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort("employee")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Employee</span>
                    {sortField === "employee" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort("department")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortField === "department" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort("policy")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Policy</span>
                    {sortField === "policy" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === "status" ? (
                      sortDirection === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
                    ) : (
                      <ArrowUpDown className="h-3 w-3 text-gray-400" />
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-agrosaf-green" />
                      <span className="ml-2 text-gray-600">
                        Loading acknowledgments...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : acknowledgments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12">
                    <div className="text-center">
                      <FileText className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No acknowledgments found
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasActiveFilters
                          ? "Try adjusting your search or filter criteria."
                          : "Get started by creating your first acknowledgment."}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedAcknowledgments.map((acknowledgment) => (
                  <tr key={acknowledgment.id || acknowledgment._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={!!selectedRowsData[acknowledgment.id || acknowledgment._id]}
                        onChange={() => handleSelectRow(acknowledgment)}
                        className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center max-w-[300px]">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div
                            className="text-sm font-medium text-gray-900 truncate capitalize"
                            title={acknowledgment.employeeName || "N/A"}
                          >
                            {acknowledgment.employeeName || "N/A"}
                          </div>
                          <div className="text-sm text-gray-500 truncate capitalize" title={acknowledgment.employeeId || "N/A"}>
                            {acknowledgment.employeeId || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span
                        className="max-w-[180px] inline-block truncate capitalize"
                        title={acknowledgment.employeeDepartment || "N/A"}
                      >
                        {acknowledgment.employeeDepartment || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-[200px]">
                      <span
                        className="block truncate capitalize"
                        title={acknowledgment.policyTitle || "N/A"}
                      >
                        {acknowledgment.policyTitle || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${acknowledgment.status === "acknowledged"
                          ? "bg-green-100 text-green-800"
                          : acknowledgment.status === "pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : acknowledgment.status === "overdue"
                              ? "bg-red-100 text-red-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {acknowledgment.status || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedAcknowledgment(acknowledgment);
                            setShowDetails(true);
                          }}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination Footer - Only show if totalItems > pageSize */}
        {
          totalItems > pageSize && (
            <div className="border-t border-gray-200 px-6 py-3 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Rows per page:
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value))}
                  className="ml-2 px-2 py-1 border rounded"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                {totalItems === 0
                  ? "0"
                  : `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalItems)} of ${totalItems}`}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )
        }
      </div>

      {/* Details Modal */}
      {showDetails && selectedAcknowledgment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 !mt-0">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-w-4xl overflow-hidden">
            <div className="mt-3 overflow-x-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Acknowledgment Details
                </h3>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedAcknowledgment(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Employee Name
                    </label>
                    <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                      {selectedAcknowledgment.employeeName || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Employee ID
                    </label>
                    <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                      {selectedAcknowledgment.employeeId || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words">
                      {selectedAcknowledgment.employeeEmail || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Department
                    </label>
                    <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                      {selectedAcknowledgment.employeeDepartment || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Policy Title
                    </label>
                    <p className="mt-1 text-sm text-gray-900 capitalize break-words">
                      {selectedAcknowledgment.policyTitle || "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${selectedAcknowledgment.status === "acknowledged"
                        ? "bg-green-100 text-green-800"
                        : selectedAcknowledgment.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : selectedAcknowledgment.status === "overdue"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {selectedAcknowledgment.status || "N/A"}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Acknowledged Date
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words">
                      {selectedAcknowledgment.acknowledgedDate ? formatDate(selectedAcknowledgment.acknowledgedDate, { includeTime: false }) : "N/A"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Created Date
                    </label>
                    <p className="mt-1 text-sm text-gray-900 break-words">
                      {selectedAcknowledgment.createdAt ? formatDate(selectedAcknowledgment.createdAt, { includeTime: false }) : "N/A"}
                    </p>
                  </div>
                </div>

                {selectedAcknowledgment.comments && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Comments
                    </label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md break-words whitespace-pre-wrap">
                      {selectedAcknowledgment.comments}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedAcknowledgment(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4 text-red-600">
                <AlertTriangle className="h-6 w-6 mr-3" />
                <h3 className="text-lg font-semibold">Confirm Bulk Deletion</h3>
              </div>

              <p className="text-gray-600 mb-2">
                Are you sure you want to delete <span className="font-bold text-red-600">{Object.keys(selectedRowsData).length}</span> selected acknowledgment records?
              </p>
              <p className="text-sm text-gray-500 mb-6 font-medium">This action cannot be undone.</p>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkDeleteConfirm(false)}
                  disabled={bulkDeleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                >
                  {bulkDeleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    `Yes, Delete ${Object.keys(selectedRowsData).length} Records`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        scopedFormatExport={{
          onExport: handleScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showAll: true,
          showDateRange: true
        }}
        loading={exportLoading}
        title="Policy Acknowledgments Export"
      />

      {
        selectedRows.length > 0 && (
          <SelectedExportFormatBar
            ref={selectedExportWrapRef}
            count={selectedRows.length}
            onExport={handleSelectedFormatExport}
            loading={exportLoading}
            isVisible={showInlineSelectedFormats}
            isEntered={inlineFormatPanelEntered}
            onClose={() => setShowInlineSelectedFormats(false)}
          />
        )
      }
    </div>
  );
};

export default AcknowledgmentsNew;
